<template>
  <div>
    <el-form
      label-position="top"
      :model="recipientInfoForm"
      ref="recipientInfoForm"
      :rules="rules"
      label-width="80px"
      size="normal"
    >
      <p class="font-bold text-xs mb-2" id="bankDetails">
        Beneficiary Bank Details
      </p>
      <hr />
      <el-form-item label="Bank Name" prop="bankName">
        <el-select
          class="w-full"
          filterable
          remote
          v-model="recipientInfoForm.bankName"
          placeholder="Search Bank"
          :remote-method="(query) => remoteMethodBanks(query)"
          :loading="loading"
          @change="changeBankName"
        >
          <el-option
            v-for="(bank, index) in bankOptions"
            :key="index"
            :label="bank.name"
            :value="bank.name"
          >
            {{ bank.name }}
          </el-option>
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-select>
      </el-form-item>
      <div class="md:grid flex flex-col md:grid-cols-4 gap-x-3">
        <div class="grid col-span-2">
          <el-form-item label="Account Number" prop="accountNo">
            <el-input
              class="w-full"
              @keydown.space.native.prevent
              min="1"
              @input="changeAccountNumber"
              v-model.trim="recipientInfoForm.accountNo"
              placeholder="Enter Account Number"
              type="text"
            ></el-input>
          </el-form-item>
        </div>
        <div class="grid col-span-2">
          <el-form-item
            label="
                  Account Name
                "
            prop="accountName"
          >
            <el-input
              class="w-full"
              min="1"
              :readonly="true"
              v-model="recipientInfoForm.accountName"
              placeholder="Enter Account Name"
              type="text"
            ></el-input>
          </el-form-item>
        </div>
      </div>
      <el-button
        @click="validateForm('recipientInfoForm')"
        :disabled="isLoading || isSavingBeneficiary"
        :loading="isLoading || isSavingBeneficiary"
        class="bg-primary w-full text-white font-normal rounded-lg py-5 shadow-lg mt-6"
      >
        {{ isSavingBeneficiary ? 'Saving' : 'Save' }}
      </el-button>
    </el-form>
  </div>
</template>

<script>
import country from '@/mixins/getCountryDetails'
import { ValidateInput, ValidateSingleSpace } from '@/utils/helpers.js'
import { ALL_COUNTRIES, ALL_NIGERIAN_BANKS } from '@/utils/constants.js'
import bulkPayout from '@/mixins/bulkPayout'
export default {
  mixins: [country, bulkPayout],
  props: {
    isSavingBeneficiary: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      isLoading: false,
      allCountries: ALL_COUNTRIES,
      allBanks: ALL_NIGERIAN_BANKS,
      bankOptions: [],
      nigerianAccountNumber: 10,
      isAccountNameError: '',
      recipientInfoForm: {
        bankName: '',
        accountNo: null,
        accountName: ''
      },
      rules: {
        accountNo: [
          {
            required: true,
            message: 'Please input account number',
            trigger: ['blur', 'change']
          },
          {
            min: 7,
            message: 'Length should be at least 7 ',
            trigger: ['blur']
          },
          { validator: this.validateAccountNo, trigger: ['blur', 'change'] },
          { validator: ValidateSingleSpace, trigger: ['blur', 'change'] }
        ],
        accountName: [
          {
            required: true,
            message: 'Please input account name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        bankName: [
          {
            required: true,
            message: "Please input Bank's name",
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  methods: {
    changeBankName (bankName) {
      this.recipientInfoForm.bankCode = this.bankOptions.find(
        (bank) => bank.name === bankName
      ).code
      if (
        this.computeValidAccountNumberLength(
          this.recipientInfoForm.accountNo.length
        )
      ) {
        this.resolveNgnAccountName(this.recipientInfoForm.accountNo)
      } else {
        this.recipientInfoForm.accountNo = ''
      }
    },
    computeValidAccountNumberLength (accountNumberLength) {
      return parseInt(accountNumberLength) === this.nigerianAccountNumber
    },
    changeAccountNumber () {
      this.isAccountNameError = ''
    },
    async validateAccountNo (_, value, callback) {
      if (this.isLoading) return // to prevent multiple calls of this endpoint simultaneously
      if (this.computeValidAccountNumberLength(value.length)) {
        if (this.recipientInfoForm.bankName) {
          // only call the endpoint if there is no account name error; so validation doesn't happen every time
          if (this.isAccountNameError !== 'false') {
            await this.resolveNgnAccountName(value)
          }
          if (this.isAccountNameError === 'true') {
            callback(new Error('Please enter correct account details'))
          }
        } else {
          callback()
        }
      } else {
        const errorMessage = '10 digits required'
        callback(new Error(errorMessage))
      }
    },
    async resolveNgnAccountName (accountNo) {
      this.recipientInfoForm.accountName = '...'

      const selectedBank = this.supportedBanks.find(
        (bank) => bank.name === this.recipientInfoForm.bankName
      )
      if (!selectedBank) {
        this.$notify.error({
          title: 'Bank name not valid',
          message: 'Please re-select from list of banks',
          position: 'top-right'
        })
        return
      }
      this.resolveNgnAccountNameCore(accountNo)
    },
    async resolveNgnAccountNameCore (number) {
      this.isLoading = true
      // resolve account name core endpoint call
      this.recipientInfoForm.accountName = '...'
      await this.$store
        .dispatch('terminal/setResolveAccountName', {
          account_number: number,
          bank_code: this.recipientInfoForm.bankCode,
          currency: this.payoutCountry.currency
        })
        .then(() => {
          this.isLoading = false
          this.isAccountNameError = 'false'
          this.recipientInfoForm.accountName =
            this.$store.state.terminal.resolveAccountName
          // here, since nsano returns empty string as account name on dev,
          // we would allow manual account name input
          if (this.isOnDevEnvironment) {
            this.isAccountNameError = 'fallback'
          }
        })
        .catch((error) => {
          this.isLoading = false
          this.recipientInfoForm.accountName = ''
          const err = error.response
            ? error?.response?.data?.error?.publicMessage
            : error
          if (err) {
            this.$notify.error({
              // zeepay currently cannot resolve an account name
              title: this.httpErrorMessage.customMessage,
              message: err,
              position: 'top-right'
            })
          }
          this.isAccountNameError = 'fallback'
        })
    },
    validateForm (recipientInfoForm) {
      this.$refs[recipientInfoForm].validate((valid) => {
        if (valid) {
          this.$emit('next', this.recipientInfoForm)
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    }
  },
  watch: {
    async 'recipientInfoForm.accountNo' (newValue) {
      this.isAccountNameError = 'false'
      if (this.computeValidAccountNumberLength(newValue.length)) {
        this.isAccountNameError = ''
        this.recipientInfoForm.accountName = ''
      }
    }
  }
}
</script>

<style scoped lang="scss">
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* For Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
